// COPIED from ptc-core web/src/shell/FeedbackMenu.tsx @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: Tailwind classes ported to Fluent makeStyles (ptc-statetax's port: this header is Fluent),
// the trigger is a subtle Fluent Button given the header's own button class by AppHeader, and i18n comes from
// src/i18n (supervision's rule).
// Behaviour is unchanged. Conformance: src/components/feedback/FeedbackWidgetConformance.test.tsx.
//
// The header entry for the feedback loop (spec §3.1): one icon, two entries. Same hand-rolled
// popover idiom as NotificationBell / AnnouncementsBoard (button + positioned panel + outside-click
// and Escape dismiss), because Fluent popup surfaces are hostile to jsdom tests.

import { useEffect, useRef, useState } from 'react';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { PersonFeedback20Regular } from '@fluentui/react-icons';
import { useTranslation } from '../../i18n';
import type { FeedbackKind } from '../../api/feedback';
import { FeedbackDialog } from './FeedbackDialog';

const useStyles = makeStyles({
  root: { position: 'relative' },
  menu: {
    position: 'absolute',
    right: 0,
    marginTop: tokens.spacingVerticalXS,
    width: '14rem',
    zIndex: 20,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase300,
    border: 'none',
    background: 'none',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
});

export function FeedbackMenu({ className }: { className?: string }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function choose(k: FeedbackKind) {
    setOpen(false);
    setKind(k);
  }

  return (
    <div ref={rootRef} className={styles.root}>
      <Button
        appearance="subtle"
        shape="circular"
        className={className}
        icon={<PersonFeedback20Regular />}
        aria-label={t('shell.feedback.menuLabel')}
        title={t('shell.feedback.menuLabel')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div role="menu" className={styles.menu}>
          <button type="button" role="menuitem" className={styles.item} onClick={() => choose('problem')}>
            {t('shell.feedback.problemTitle')}
          </button>
          <button type="button" role="menuitem" className={styles.item} onClick={() => choose('improvement')}>
            {t('shell.feedback.improvementTitle')}
          </button>
        </div>
      )}
      <FeedbackDialog kind={kind} onClose={() => setKind(null)} />
    </div>
  );
}
